import { useGridRowPinHold, type GridRowPinHold } from '@sb/ui/grid-scroll';
import { defer, finalize, type Observable } from 'rxjs';

export interface AutosaveRowHold {
  /** A form change entered the pipeline. Returns its id for the later stages. */
  changed(): number;
  passed(change: number): void;
  rejected(change: number): void;
  save<T>(change: number, save$: Observable<T>): Observable<T>;
  /** The pipeline has errored and will see no more changes. */
  stopped(): void;
}

/**
 * Keeps a timesheet row pinned while any of its autosaves is unfinished. It follows the row's change
 * pipeline — check, then a debounce, then the save — where each stage keeps only the newest change:
 *
 * - a newer change cancels one still in its check;
 * - a newer change that passes its check replaces one waiting in the debounce, while one that is
 *   rejected leaves it to be saved;
 * - a newer save cancels one in flight.
 *
 * So the row is released only once no change is being checked, none is waiting to be saved and no
 * save is in flight — not when any one change settles, which could let the row unmount under a newer
 * change still in its check, or under an older save.
 *
 * Call from an injection context, unless a `rowHold` is passed in.
 */
export function useAutosaveRowHold(rowHold: GridRowPinHold = useGridRowPinHold()): AutosaveRowHold {
  let checking: number | null = null;
  let awaitingSave: number | null = null;
  let savesInFlight = 0;
  let lastChange = 0;

  const releaseIfIdle = (): void => {
    if (checking === null && awaitingSave === null && savesInFlight === 0) {
      rowHold.release();
    }
  };

  return {
    changed: () => {
      lastChange += 1;
      checking = lastChange;
      rowHold.hold();

      return lastChange;
    },
    passed: (change) => {
      if (checking === change) {
        checking = null;
        awaitingSave = change;
      }
    },
    rejected: (change) => {
      if (checking === change) {
        checking = null;
        releaseIfIdle();
      }
    },
    save: (change, save$) =>
      defer(() => {
        if (awaitingSave === change) {
          awaitingSave = null;
        }

        savesInFlight += 1;

        return save$.pipe(
          finalize(() => {
            savesInFlight -= 1;
            releaseIfIdle();
          }),
        );
      }),
    stopped: () => {
      checking = null;
      awaitingSave = null;
      rowHold.release();
    },
  };
}
